/**
 * Authentication module entry point.
 *
 * Selects the authentication provider by authMode (T012):
 * - MockAuthProvider for local development (authMode === 'mock')
 * - OidcAuthProvider (real Cognito) for dev/prod (authMode === 'oidc')
 *
 * Provider-agnostic: works with Cognito or any OIDC provider, preserving PKCE code flow,
 * silent renew, and Cognito-specific /logout handling.
 */

import React from 'react';
import type { AuthConfig } from '../config/auth';
import { MockAuthProvider } from './MockAuthProvider';
import { OidcAuthProvider } from './AuthProvider';

// Re-export authentication hooks
export {
  useAuth,
  useUser,
  useHasRole,
  useAccessToken,
  useCanonicalClaims,
  useProviderLogout,
} from './hooks';
export type { AuthHook, UserProfile, ProviderLogoutHook } from './hooks';

// Re-export canonical claims types + helpers
export type { CanonicalUserClaims, ClaimMappingConfig } from './claim-mapping';
export { mapClaims, mapClaimsForProvider, hasGroup, isAdmin, getDisplayName } from './claim-mapping';

// Re-export provider components for direct usage if needed
export { MockAuthProvider } from './MockAuthProvider';
export { OidcAuthProvider } from './AuthProvider';

// Re-export provider utilities
export { getAuthProvider, isMockAuthEnabled, buildOidcConfig, buildLogoutArgs } from './providers/oidc';
export type { AuthProviderType, OidcProviderConfig, SignoutRedirectArgs } from './providers/types';

interface AuthProviderProps {
  children: React.ReactNode;
  /** The single consolidated auth configuration. */
  config: AuthConfig;
}

/**
 * Environment-aware authentication provider.
 *
 * Renders MockAuthProvider (mock) or OidcAuthProvider (oidc) based on the resolved AuthConfig.
 */
export function AuthProvider({ children, config }: AuthProviderProps): JSX.Element {
  if (config.isMockAuth) {
    return React.createElement(MockAuthProvider, {
      mockEmail: config.mockUser?.email,
      mockName: config.mockUser?.name,
      children,
    });
  }

  if (!config.oidcConfig) {
    throw new Error('OIDC configuration required for non-mock authentication');
  }

  return React.createElement(OidcAuthProvider, { config: config.oidcConfig, children });
}
